import { Button } from "@/components/ui/button";
import type { AppInfo } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { ChevronRight, Loader2, Unplug } from "lucide-react";
import type { ReactNode } from "react";
import type { ConnectionState, ConnectionStatus } from "@/lib/connection-status";
import type { ConnectionDef } from "./connections-lib";

// Building blocks shared by the Connections home, each connection's page, the
// workspace's Connections, and the catalog tile that links to a connection.

const LOGO_SIZES = {
  md: { tile: "size-9 rounded-lg", logo: "size-5" },
  lg: { tile: "size-12 rounded-xl", logo: "size-7" },
} as const;

function ConnectionLogo({
  Logo,
  size,
}: {
  Logo: ConnectionDef["Logo"];
  size: keyof typeof LOGO_SIZES;
}) {
  const s = LOGO_SIZES[size];
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center bg-background text-foreground ring-1 ring-border",
        s.tile,
      )}
    >
      <Logo className={s.logo} />
    </div>
  );
}

const DOT: Record<ConnectionState, string> = {
  connected: "bg-emerald-500",
  available: "bg-sky-500",
  elsewhere: "bg-amber-500",
  off: "bg-muted-foreground/40",
};

export function StatusDot({ state }: { state: ConnectionState }) {
  return <span className={cn("size-1.5 shrink-0 rounded-full", DOT[state])} />;
}

export function StatusLine({ status, className }: { status: ConnectionStatus | undefined; className?: string }) {
  return (
    <p className={cn("flex h-4 min-w-0 items-center gap-1.5 text-xs text-muted-foreground", className)}>
      {status && !status.loading && (
        <>
          <StatusDot state={status.state} />
          <span className={cn("truncate", status.state === "connected" && "text-foreground")}>{status.label}</span>
        </>
      )}
    </p>
  );
}

// One row on a Connections list: the whole row opens the connection's page.
export function ConnectionRow({
  def,
  status,
  to,
  params,
  detail,
}: {
  def: Pick<ConnectionDef, "name" | "Logo" | "description">;
  status: ConnectionStatus | undefined;
  to: "/orgs/$orgId/containers/$containerId/connections/$connection" | "/orgs/$orgId/connections/$connection";
  params: Record<string, string>;
  // Replaces the status line (the workspace list says who uses a connection).
  detail?: ReactNode;
}) {
  const connected = status?.state === "connected";
  return (
    <Link
      to={to}
      params={params as never}
      className="group flex items-center gap-3 px-4 py-3 outline-none transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      <ConnectionLogo Logo={def.Logo} size="md" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-sm font-medium leading-tight">{def.name}</p>
        {detail ?? <StatusLine status={status} />}
      </div>
      <span className="hidden shrink-0 text-xs font-medium text-muted-foreground group-hover:text-foreground sm:inline">
        {connected ? "Manage" : "Set up"}
      </span>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

export function ConnectionList({ children }: { children: ReactNode }) {
  return <div className="divide-y rounded-lg border bg-background">{children}</div>;
}

export function ConnectionSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="space-y-0.5">
        <h3 className="text-sm font-semibold">{title}</h3>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {children}
    </section>
  );
}

export function ConnectionHeader({
  def,
  status,
  actions,
}: {
  def: Pick<ConnectionDef, "name" | "Logo" | "description">;
  // Shown under the name; the description when absent.
  status?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start gap-4">
      <ConnectionLogo Logo={def.Logo} size="lg" />
      <div className="min-w-0 flex-1 space-y-1 pt-0.5">
        <h2 className="text-lg font-semibold leading-tight">{def.name}</h2>
        <div className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
          {status ?? def.description}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

// Catalog tile for a hub entry with `connectedBy: "app"`, sized like a hub
// tile. It opens the connection's page instead of starting a connect flow.
export function ConnectionTile({
  app,
  def,
  status,
  orgId,
  containerId,
}: {
  app: AppInfo;
  def: ConnectionDef;
  status: ConnectionStatus | undefined;
  orgId: string;
  containerId: string;
}) {
  const connected = status?.state === "connected";
  return (
    <Link
      to="/orgs/$orgId/containers/$containerId/connections/$connection"
      params={{ orgId, containerId, connection: def.id }}
      className="group flex w-full items-center gap-3 rounded-lg border bg-background p-4 text-left outline-none transition-[background-color,scale] hover:bg-muted/40 active:scale-[0.96] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      {app.logoUrl ? (
        <img src={app.logoUrl} alt="" className="size-8 shrink-0 rounded object-contain" />
      ) : (
        <def.Logo className="size-8 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold leading-tight">{app.name}</p>
        <p className="truncate text-xs text-muted-foreground">{connected ? status.label : app.description}</p>
      </div>
      {connected && <StatusDot state="connected" />}
      <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

// Facts about a connection, one per row: what it is on the left, the value in
// the middle, and an optional action (Edit, Change on GitHub) on the right.
export function DetailList({ children }: { children: ReactNode }) {
  return <dl className="divide-y rounded-lg border text-sm">{children}</dl>;
}

export function DetailRow({ term, action, children }: { term: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
      <dt className="w-32 shrink-0 text-muted-foreground">{term}</dt>
      <dd className="min-w-0 flex-1 break-words">{children}</dd>
      {action}
    </div>
  );
}

const NOTICE_TONE = {
  neutral: "bg-muted/40",
  info: "bg-sky-50 ring-sky-200 dark:bg-sky-500/10 dark:ring-sky-500/30",
  warning: "bg-amber-50 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/30",
} as const;

// A short state the page is in (held by another Boo, offline, needs
// reauthorizing), with the action that resolves it.
export function Notice({
  tone = "neutral",
  children,
  action,
}: {
  tone?: keyof typeof NOTICE_TONE;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 rounded-lg px-4 py-3 text-sm ring-1 ring-border ring-inset",
        NOTICE_TONE[tone],
      )}
    >
      <div className="min-w-0 flex-1 space-y-1">{children}</div>
      {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
    </div>
  );
}

// Header action that cuts a connection, styled the same on every page.
export function DisconnectButton({ pending, onClick }: { pending: boolean; onClick: () => void }) {
  return (
    <Button
      size="sm"
      variant="ghost"
      className="gap-1.5 text-destructive hover:text-destructive"
      disabled={pending}
      onClick={onClick}
    >
      {pending ? <Loader2 className="size-3.5 animate-spin" /> : <Unplug className="size-3.5" />}
      Disconnect
    </Button>
  );
}
